import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormSubmitHandler,
  FormField,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {usePostServiceOrderItemsVatChangeMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, useGetVatRatesOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {bulkVatChangeFormSchema, BulkVatChangeFormType} from '../types/bulkVatChangeFormSchema';

interface BulkVatChangeFormProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  items: {serviceJobId: string; serviceItemId: string}[];
  onSubmit: VoidFunction;
  onClose: VoidFunction;
}

export function BulkVatChangeForm(props: BulkVatChangeFormProps) {
  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const [postServiceOrderItemsVatChange] = usePostServiceOrderItemsVatChangeMutation();

  const handleSubmit: FormSubmitHandler<BulkVatChangeFormType> = async (data) =>
    await postServiceOrderItemsVatChange({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        serviceItems: props.items.map((item) => ({
          serviceItemId: item.serviceItemId,
          serviceJobId: item.serviceJobId,
        })),
        vatType: data.vatType,
      },
    })
      .unwrap()
      .then(() => {
        props.onSubmit();
        props.onClose();
      })
      .catch(handleApiError);

  return (
    <Form<BulkVatChangeFormType>
      experimentalZodSchema={bulkVatChangeFormSchema}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            isRequired
            isNotClearable
            control={control}
            label={i18n.t('general.labels.vat')}
            name="vatType"
            type="choice"
            options={vatOptions}
            data-testid={suffixTestId('vatType', props)}
            menuInPortal
          />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              variant="secondary"
              onClick={props.onClose}
              data-testid={suffixTestId('discardButton', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.confirm')}
              data-testid={suffixTestId('confirmButton', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
